// src/utils/dateUtils.ts

export function parseYmdLocal(ymd: string): Date | null {
  const m = String(ymd || "").trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [, yyyy, mm, dd] = m;
  return new Date(Number(yyyy), Number(mm) - 1, Number(dd)); // LOCAL midnight
}

function formatDateMMDDYYYY(d: Date) {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = String(d.getFullYear());
  return `${mm}/${dd}/${yyyy}`;
}

/**
 * Display for date-only "YYYY-MM-DD" without UTC shifting.
 * Apple Style: always MM/DD/YYYY
 */
export function formatMMDDYYYYFromYMD(ymd?: string | null) {
  if (!ymd) return "—";
  const d = parseYmdLocal(ymd);
  if (!d) return String(ymd);
  return formatDateMMDDYYYY(d);
}

/**
 * Accepts "MM/DD/YYYY" OR "MM-DD-YYYY" (1–2 digit mm/dd ok).
 * Returns YYYY-MM-DD (zero-padded).
 */
export function mdyToYmd(mdy: string) {
  const s = String(mdy || "").trim();
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!m) return "";
  const [, mm, dd, yyyy] = m;
  return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
}

export function ymdTodayLocal() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * For typing assistance (controlled inputs).
 * Apple Style: show MM/DD/YYYY while user types.
 */
export function normalizeMDY(input: string) {
  const digits = String(input || "").replace(/\D/g, "").slice(0, 8); // MMDDYYYY
  const mm = digits.slice(0, 2);
  const dd = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);

  let out = mm;
  if (digits.length >= 3) out += "/" + dd;
  if (digits.length >= 5) out += "/" + yyyy;
  return out;
}

// Date-only guard: ensures YYYY-MM-DD (no timezone)
// Use before sending date-only strings to the API.
export function assertYmd(ymd: string) {
  const s = String(ymd || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    throw new Error(`Expected YYYY-MM-DD, got "${ymd}"`);
  }
  return s;
}

/**
 * Locale display for date-only strings without UTC shifting.
 * Apple Style: DO NOT use locale; always MM/DD/YYYY
 */
export function formatDateYMDToLocale(ymd?: string | null) {
  return formatMMDDYYYYFromYMD(ymd);
}

/**
 * Safe display: works for BOTH date-only "YYYY-MM-DD" and datetime strings.
 * Apple Style: always MM/DD/YYYY (padded).
 */
export function formatAnyDateToLocale(input?: string | null) {
  if (!input) return "—";

  const s = String(input).trim();

  // Date-only: format without UTC shift
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return formatMMDDYYYYFromYMD(s);
  }

  // Datetime: let JS parse, but format consistently
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : formatDateMMDDYYYY(d);
}

// First day of current month in local time, as YYYY-MM-DD
export function ymdMonthStartLocal(d = new Date()) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${yyyy}-${mm}-01`;
}
